import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as http from '@/api/http';
import type { StaffUser } from '@/api/types';
import { AuthContext, type AuthStatus } from './context';

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>(() =>
    http.getSession() ? 'signedIn' : 'loading',
  );
  const [staff, setStaff] = useState<StaffUser | null>(() => http.getSession()?.staff ?? null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const off = http.onSessionChange((tokens) => {
      setStaff((prev) => {
        if (prev && !tokens) setExpired(true);
        return tokens?.staff ?? null;
      });
      setStatus(tokens ? 'signedIn' : 'signedOut');
    });
    // On load: try the refresh cookie.
    if (!http.getSession()) {
      http
        .refreshSession()
        .catch(() => null)
        .then((t) => {
          if (!t) setStatus('signedOut');
        });
    }
    return () => {
      off();
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const t = await http.login(email, password);
    setExpired(false);
    return t;
  }, []);

  const acceptInvite = useCallback(async (token: string, name: string, password: string) => {
    const t = await http.acceptInvite(token, name, password);
    setExpired(false);
    return t;
  }, []);

  const logout = useCallback(async () => {
    try {
      await http.logout();
    } finally {
      setExpired(false);
      qc.clear();
    }
  }, [qc]);

  const value = useMemo(
    () => ({ status, staff, login, acceptInvite, logout, expired }),
    [status, staff, login, acceptInvite, logout, expired],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
